import { Product } from './products'

/**
 * Admin mock data — DEV ONLY.
 *
 * This file powers the /admin section during the design/dev phase.
 * Replace with real backend data before launch.
 */

export type OrderStatus =
  | 'paid'
  | 'fulfilled'
  | 'shipped'
  | 'delivered'
  | 'refunded'
  | 'cancelled'
export type PaymentMethod =
  | 'Visa'
  | 'Mastercard'
  | 'Amex'
  | 'Discover'
  | 'PayPal'
  | 'ApplePay'
  | 'GooglePay'
  | 'Card'

export interface OrderLineItem {
  productName: string
  sku: string
  qty: number
  unitPrice: number
  /** Optional YarboCare plan attached to this line */
  protection?: { plan: string; price: number }
}

export interface AdminOrder {
  id: string
  placedAt: string // ISO
  status: OrderStatus
  customer: {
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  shipping: {
    line1: string
    line2?: string
    city: string
    state: string
    zip: string
    country: string
  }
  billing: {
    line1: string
    line2?: string
    city: string
    state: string
    zip: string
    country: string
  }
  payment: {
    method: PaymentMethod
    last4: string
  }
  items: OrderLineItem[]
  shippingCost: number
  tax: number
  notes?: string
  discountCode?: string
  discountAmount?: number
  subtotalBeforeDiscount?: number
  subtotalAfterDiscount?: number
}

export const MOCK_ORDERS: AdminOrder[] = [
  {
    id: 'MR-10458',
    placedAt: '2026-05-26T14:08:00Z',
    status: 'paid',
    customer: {
      firstName: 'Daniel',
      lastName: 'Whitlock',
      email: 'daniel.whitlock@example.com',
      phone: '+1 (313) 555-0118',
    },
    shipping: {
      line1: '482 Maple Ridge Dr',
      city: 'Ann Arbor',
      state: 'MI',
      zip: '48104',
      country: 'USA',
    },
    billing: {
      line1: '482 Maple Ridge Dr',
      city: 'Ann Arbor',
      state: 'MI',
      zip: '48104',
      country: 'USA',
    },
    payment: { method: 'Visa', last4: '4421' },
    items: [
      {
        productName: 'Yarbo Lawn Mower Pro + Snow Blower + Blower',
        sku: 'YRB-BNDL-PRO-3',
        qty: 1,
        unitPrice: 7499,
        protection: {
          plan: '3-Year Accidental Damage + 3-Year Extended Warranty',
          price: 649,
        },
      },
    ],
    shippingCost: 0,
    tax: 480.94,
    notes: 'Leave at side garage. Customer is home Saturdays.',
  },
  {
    id: 'MR-10457',
    placedAt: '2026-05-26T11:42:00Z',
    status: 'shipped',
    customer: {
      firstName: 'Priya',
      lastName: 'Khatri',
      email: 'priya.k@example.com',
      phone: '+1 (612) 555-0144',
    },
    shipping: {
      line1: '910 Oakwood Ln',
      line2: 'Apt 3B',
      city: 'Minneapolis',
      state: 'MN',
      zip: '55403',
      country: 'USA',
    },
    billing: {
      line1: '910 Oakwood Ln',
      line2: 'Apt 3B',
      city: 'Minneapolis',
      state: 'MN',
      zip: '55403',
      country: 'USA',
    },
    payment: { method: 'Mastercard', last4: '7732' },
    items: [
      {
        productName: 'Yarbo Lawn Mower',
        sku: 'YRB-LM-STD',
        qty: 1,
        unitPrice: 4199,
      },
      {
        productName: 'Cutting Blades & Bolts - 40 Pcs',
        sku: 'YRB-ACC-BLDS-40',
        qty: 2,
        unitPrice: 39,
      },
    ],
    shippingCost: 0,
    tax: 285.23,
  },
  {
    id: 'MR-10456',
    placedAt: '2026-05-25T19:21:00Z',
    status: 'fulfilled',
    customer: {
      firstName: 'Marcus',
      lastName: 'Hollander',
      email: 'mhollander@example.com',
      phone: '+1 (216) 555-0188',
    },
    shipping: {
      line1: '1247 Birch Hollow',
      city: 'Cleveland',
      state: 'OH',
      zip: '44113',
      country: 'USA',
    },
    billing: {
      line1: '1247 Birch Hollow',
      city: 'Cleveland',
      state: 'OH',
      zip: '44113',
      country: 'USA',
    },
    payment: { method: 'Amex', last4: '1005' },
    items: [
      {
        productName: 'Yarbo Snow Blower',
        sku: 'YRB-SB-STD',
        qty: 1,
        unitPrice: 4999,
        protection: { plan: '3-Year Extended Warranty', price: 299 },
      },
      {
        productName: 'Snow Plow Blade',
        sku: 'YRB-ACC-PLOW',
        qty: 1,
        unitPrice: 159,
      },
    ],
    shippingCost: 0,
    tax: 333.72,
  },
  {
    id: 'MR-10455',
    placedAt: '2026-05-25T08:55:00Z',
    status: 'delivered',
    customer: {
      firstName: 'Elena',
      lastName: 'Brooks',
      email: 'elena.brooks@example.com',
      phone: '+1 (208) 555-0166',
    },
    shipping: {
      line1: '76 Pinecrest Way',
      city: 'Boise',
      state: 'ID',
      zip: '83702',
      country: 'USA',
    },
    billing: {
      line1: '76 Pinecrest Way',
      city: 'Boise',
      state: 'ID',
      zip: '83702',
      country: 'USA',
    },
    payment: { method: 'Visa', last4: '9912' },
    items: [
      {
        productName: 'Yarbo Lawn Mower + Snow Blower + Blower',
        sku: 'YRB-BNDL-STD-3',
        qty: 1,
        unitPrice: 5999,
      },
      {
        productName: 'Track Grease',
        sku: 'YRB-ACC-GRSE',
        qty: 1,
        unitPrice: 49,
      },
      { productName: 'Tow Hitch', sku: 'YRB-ACC-TOW', qty: 1, unitPrice: 69 },
    ],
    shippingCost: 0,
    tax: 367.02,
  },
  {
    id: 'MR-10454',
    placedAt: '2026-05-24T16:13:00Z',
    status: 'paid',
    customer: {
      firstName: 'Jamal',
      lastName: 'Rivers',
      email: 'jamal.rivers@example.com',
      phone: '+1 (404) 555-0143',
    },
    shipping: {
      line1: '305 Atlantic Pkwy',
      city: 'Atlanta',
      state: 'GA',
      zip: '30308',
      country: 'USA',
    },
    billing: {
      line1: '12 Office Sq',
      line2: 'Suite 410',
      city: 'Atlanta',
      state: 'GA',
      zip: '30309',
      country: 'USA',
    },
    payment: { method: 'PayPal', last4: 'PYP' },
    items: [
      {
        productName: 'Yarbo Lawn Mower Pro',
        sku: 'YRB-LM-PRO',
        qty: 1,
        unitPrice: 5599,
        protection: {
          plan: '1-Year Accidental Damage + 3-Year Extended Warranty',
          price: 449,
        },
      },
    ],
    shippingCost: 0,
    tax: 364.86,
  },
  {
    id: 'MR-10453',
    placedAt: '2026-05-24T10:02:00Z',
    status: 'refunded',
    customer: {
      firstName: 'Olivia',
      lastName: 'Park',
      email: 'o.park@example.com',
      phone: '+1 (503) 555-0102',
    },
    shipping: {
      line1: '4400 NE Halsey',
      city: 'Portland',
      state: 'OR',
      zip: '97213',
      country: 'USA',
    },
    billing: {
      line1: '4400 NE Halsey',
      city: 'Portland',
      state: 'OR',
      zip: '97213',
      country: 'USA',
    },
    payment: { method: 'Discover', last4: '6618' },
    items: [
      {
        productName: 'Yarbo Blower',
        sku: 'YRB-BLW-STD',
        qty: 1,
        unitPrice: 4799,
      },
    ],
    shippingCost: 0,
    tax: 311.94,
    notes: 'Customer changed mind — full refund issued 5/24.',
  },
  {
    id: 'MR-10452',
    placedAt: '2026-05-23T22:48:00Z',
    status: 'cancelled',
    customer: {
      firstName: 'Trevor',
      lastName: 'Adebayo',
      email: 'tadebayo@example.com',
      phone: '+1 (832) 555-0177',
    },
    shipping: {
      line1: '1860 Heights Blvd',
      city: 'Houston',
      state: 'TX',
      zip: '77008',
      country: 'USA',
    },
    billing: {
      line1: '1860 Heights Blvd',
      city: 'Houston',
      state: 'TX',
      zip: '77008',
      country: 'USA',
    },
    payment: { method: 'ApplePay', last4: 'APL' },
    items: [
      {
        productName: 'Yarbo Lawn Mower + Snow Blower',
        sku: 'YRB-BNDL-STD-2',
        qty: 1,
        unitPrice: 5199,
      },
    ],
    shippingCost: 0,
    tax: 0,
    notes: 'Duplicate order — buyer placed correct one as MR-10454.',
  },
]

export type TemplateChannel = 'email' | 'sms'
export type TemplateTrigger =
  | 'order_confirmation'
  | 'shipping_update'
  | 'out_for_delivery'
  | 'delivery_confirmation'
  | 'order_cancellation'
  | 'refund_issued'
  | 'yarbocare_activated'

export interface NotificationTemplate {
  id: string
  channel: TemplateChannel
  trigger: TemplateTrigger
  triggerLabel: string
  /** Email only */
  subject?: string
  /** Body of the message. Supports {{variables}} */
  body: string
  active: boolean
}

export const DEFAULT_TEMPLATES: NotificationTemplate[] = [
  {
    id: 'tpl-001',
    channel: 'email',
    trigger: 'order_confirmation',
    triggerLabel: 'Order Confirmation',
    subject: 'Your Merem Robotics order {{order.id}} is confirmed',
    body: `Hi {{customer.firstName}},

Thanks for ordering from Merem Robotics! Your Yarbo order {{order.id}} is confirmed and we'll send tracking as soon as it ships.

Order summary:
{{order.lineItems}}

Order total: {{order.total}}
Shipping to: {{shipping.address}}

If you added YarboCare, your protection plan will be activated once the order ships.

Questions? Reply to this email or visit merem-robotics.com/contact.

— Merem Robotics`,
    active: true,
  },
  {
    id: 'tpl-002',
    channel: 'sms',
    trigger: 'order_confirmation',
    triggerLabel: 'Order Confirmation',
    body: 'Merem Robotics: Order {{order.id}} confirmed. Total {{order.total}}. We\u2019ll text tracking when it ships. Reply STOP to opt out.',
    active: true,
  },
  {
    id: 'tpl-003',
    channel: 'email',
    trigger: 'shipping_update',
    triggerLabel: 'Shipping Notification',
    subject: 'Your Yarbo order {{order.id}} has shipped',
    body: `Hi {{customer.firstName}},

Your Yarbo order {{order.id}} is on its way.

Carrier: {{shipping.carrier}}
Tracking: {{shipping.trackingNumber}}
Estimated delivery: {{shipping.eta}}

We'll let you know when it arrives. Thanks for choosing Merem Robotics.

— Merem Robotics`,
    active: true,
  },
  {
    id: 'tpl-004',
    channel: 'sms',
    trigger: 'shipping_update',
    triggerLabel: 'Shipping Notification',
    body: 'Merem Robotics: Order {{order.id}} has shipped via {{shipping.carrier}}. Track: {{shipping.trackingShortUrl}}',
    active: true,
  },
  {
    id: 'tpl-005',
    channel: 'sms',
    trigger: 'out_for_delivery',
    triggerLabel: 'Out for Delivery',
    body: 'Your Yarbo order {{order.id}} is out for delivery today. Make sure someone is available to receive it. — Merem Robotics',
    active: true,
  },
  {
    id: 'tpl-006',
    channel: 'email',
    trigger: 'delivery_confirmation',
    triggerLabel: 'Delivery Confirmation',
    subject: 'Your Yarbo order {{order.id}} has been delivered',
    body: `Hi {{customer.firstName}},

Your Yarbo order {{order.id}} was delivered on {{delivery.date}}.

If anything looks off, reply to this email and we\u2019ll help you out.

— Merem Robotics`,
    active: true,
  },
  {
    id: 'tpl-007',
    channel: 'email',
    trigger: 'order_cancellation',
    triggerLabel: 'Order Cancellation',
    subject: 'Your Merem Robotics order {{order.id}} has been cancelled',
    body: `Hi {{customer.firstName}},

Your order {{order.id}} has been cancelled per your request. Any authorized charges will be released back to your payment method.

If this was a mistake, reply to this email and we\u2019ll help.

— Merem Robotics`,
    active: true,
  },
  {
    id: 'tpl-008',
    channel: 'email',
    trigger: 'refund_issued',
    triggerLabel: 'Refund Issued',
    subject: 'Refund processed for order {{order.id}}',
    body: `Hi {{customer.firstName}},

We've processed a refund of {{refund.amount}} for order {{order.id}}. It may take 5\u20137 business days to appear on your statement.

— Merem Robotics`,
    active: true,
  },
  {
    id: 'tpl-009',
    channel: 'email',
    trigger: 'yarbocare_activated',
    triggerLabel: 'YarboCare Protection Activated',
    subject: 'YarboCare protection activated for order {{order.id}}',
    body: `Hi {{customer.firstName}},

Your YarboCare protection plan is now active.

Plan: {{yarbocare.planName}}
Coverage starts: {{yarbocare.startDate}}
Coverage ends: {{yarbocare.endDate}}

Plan details, terms, and claim instructions are managed through YarboCare / XCotton.

— Merem Robotics`,
    active: true,
  },
]

export const TEMPLATE_VARIABLES = [
  '{{customer.firstName}}',
  '{{customer.lastName}}',
  '{{customer.email}}',
  '{{customer.phone}}',
  '{{order.id}}',
  '{{order.total}}',
  '{{order.lineItems}}',
  '{{shipping.address}}',
  '{{shipping.carrier}}',
  '{{shipping.trackingNumber}}',
  '{{shipping.trackingShortUrl}}',
  '{{shipping.eta}}',
  '{{delivery.date}}',
  '{{refund.amount}}',
  '{{yarbocare.planName}}',
  '{{yarbocare.startDate}}',
  '{{yarbocare.endDate}}',
  '{{customer_first_name}}',
  '{{customer_last_name}}',
  '{{customer_email}}',
  '{{order_number}}',
  '{{order_total}}',
  '{{order_status}}',
  '{{shipping_address}}',
  '{{tracking_number}}',
  '{{product_names}}',
  '{{discount_code}}',
  '{{yarbocare_plan}}',
  '{{store_name}}',
  '{{support_email}}',
  '{{support_phone}}',
]

export const STATUS_META: Record<
  OrderStatus,
  { label: string; bg: string; text: string }
> = {
  paid: { label: 'Paid', bg: 'bg-blue-100', text: 'text-blue-800' },
  fulfilled: {
    label: 'Fulfilled',
    bg: 'bg-indigo-100',
    text: 'text-indigo-800',
  },
  shipped: { label: 'Shipped', bg: 'bg-amber-100', text: 'text-amber-800' },
  delivered: { label: 'Delivered', bg: 'bg-green-100', text: 'text-green-800' },
  refunded: { label: 'Refunded', bg: 'bg-gray-200', text: 'text-gray-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100', text: 'text-red-700' },
}
